"use client";

import { FormEvent, useState } from "react";
import { site } from "@/lib/data";

type Props = {
  subjectPrefix: string;
  submitLabel: string;
  includeRole?: boolean;
};

export function MailtoForm({
  subjectPrefix,
  submitLabel,
  includeRole = false,
}: Props) {
  const [sent, setSent] = useState(false);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const phone = String(data.get("phone") ?? "").trim();
    const role = String(data.get("role") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();

    const lines = [
      `Name: ${name}`,
      `Email: ${email}`,
      `Phone: ${phone}`,
      includeRole ? `Interest: ${role}` : "",
      "",
      message,
    ].filter(Boolean);

    const mailto = `mailto:${site.email}?subject=${encodeURIComponent(
      `${subjectPrefix} — ${name}`,
    )}&body=${encodeURIComponent(lines.join("\n"))}`;

    window.location.href = mailto;
    setSent(true);
  }

  if (sent) {
    return (
      <p
        className="rounded-2xl border border-line bg-surface p-6 text-sm leading-6 text-muted shadow-[var(--shadow-card)]"
        role="status"
      >
        Your email app should open with the message. If it does not, write to{" "}
        <a className="font-medium text-red" href={`mailto:${site.email}`}>
          {site.email}
        </a>
        .
      </p>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-2xl border border-line bg-surface p-6 shadow-[var(--shadow-card)] sm:p-8"
    >
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-ink">
          Name
        </label>
        <input
          id="name"
          name="name"
          required
          autoComplete="name"
          className="input-field mt-1.5"
        />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-ink">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          className="input-field mt-1.5"
        />
      </div>
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-ink">
          Phone
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          className="input-field mt-1.5"
        />
      </div>
      {includeRole ? (
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-ink">
            Role of interest
          </label>
          <input id="role" name="role" className="input-field mt-1.5" />
        </div>
      ) : null}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-ink">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          className="input-field mt-1.5 min-h-32 py-3"
        />
      </div>
      <button type="submit" className="btn-primary">
        {submitLabel}
      </button>
      <p className="text-xs text-muted">
        This opens your email application. Nothing is stored on this website.
      </p>
    </form>
  );
}
